import { Message, ContentBlock, ToolUseBlock, DocAttachment } from './types'
import { getProvider } from './providers'
import { toolDefinitions, executeTool } from './toolRegistry'
import { getSkill } from './skills'
import { store, currentApiKey, setAbortController, abortController } from './store'

const MAX_STEPS = 12
const MAX_TOKENS = 4096

function buildSystem(): string {
	let s =
		'You are the AI assistant embedded in Content Hub, a headless CMS editor. ' +
		'Help the user manage and edit content. Use the available tools to inspect ' +
		'the current entry, edit fields, and navigate the app on the user\'s behalf. ' +
		'Prefer taking action with a tool over telling the user to do it manually. ' +
		'The user may attach PDF documents; read them and use the field tools to ' +
		'check or fill entry fields based on their content. ' +
		'When the user talks about "this" or "that", etc., they probably mean the ' +
		'page you live on, you should read the fields to understand its contents ' +
		'before asking for clarification. ' +
		'Keep replies short. When you change content, briefly say what you changed.'
	const skill = getSkill(store.activeSkillId)
	if (skill) s += `\n\n## Active skill: ${ skill.name }\n${ skill.instructions }`
	return s
}

export function stop() {
	if (abortController) abortController.abort()
}

export async function sendMessage(text: string, attachments: DocAttachment[] = []) {
	if (store.running) return
	if (!text && !attachments.length) return
	const apiKey = currentApiKey()
	if (!apiKey) {
		store.error = 'No API key set. Open settings and add your key.'
		return
	}

	store.error = null
	const content: ContentBlock[] = []
	for (const a of attachments) content.push({ type: 'document', mediaType: a.mediaType, data: a.data, name: a.name })
	if (text) content.push({ type: 'text', text })
	store.messages.push({ role: 'user', content })

	store.running = true
	const controller = new AbortController()
	setAbortController(controller)

	try {
		const provider = getProvider(store.config.provider)

		for (let step = 0; step < MAX_STEPS; step++) {
			// The request sees everything so far; the assistant shell we push
			// next is streamed into but not part of the request history.
			const requestMessages: Message[] = store.messages.map(m => m)

			const assistant: Message = { role: 'assistant', content: [] }
			store.messages.push(assistant)

			// Reconstruct the assistant message from indexed stream events.
			const blockByIndex: { [i: number]: ContentBlock } = {}
			const jsonByIndex: { [i: number]: string } = {}
			let stopReason = 'end_turn'
			let streamError: string | null = null

			for await (const evt of provider.stream({
				apiKey,
				model: store.config.model,
				system: buildSystem(),
				messages: requestMessages,
				tools: toolDefinitions(),
				maxTokens: MAX_TOKENS,
				signal: controller.signal,
			})) {
				if (evt.type === 'text_delta') {
					let block = blockByIndex[evt.index] as any
					if (!block || block.type !== 'text') {
						block = { type: 'text', text: '' }
						blockByIndex[evt.index] = block
						assistant.content.push(block)
					}
					block.text += evt.text
				}
				else if (evt.type === 'tool_use_start') {
					const block: ToolUseBlock = { type: 'tool_use', id: evt.id, name: evt.name, input: {} }
					blockByIndex[evt.index] = block
					jsonByIndex[evt.index] = ''
					assistant.content.push(block)
				}
				else if (evt.type === 'tool_use_input_delta') {
					jsonByIndex[evt.index] = (jsonByIndex[evt.index] ?? '') + evt.partialJson
				}
				else if (evt.type === 'block_stop') {
					const block = blockByIndex[evt.index] as any
					if (block && block.type === 'tool_use') {
						try { block.input = jsonByIndex[evt.index] ? JSON.parse(jsonByIndex[evt.index]) : {} }
						catch { block.input = {} }
					}
				}
				else if (evt.type === 'done') {
					stopReason = evt.stopReason
				}
				else if (evt.type === 'error') {
					streamError = evt.error
				}
			}

			if (streamError) {
				store.error = streamError
				// Drop the empty/partial assistant turn on hard errors.
				if (!assistant.content.length) store.messages.pop()
				break
			}

			if (stopReason !== 'tool_use') break

			// Run every requested tool and feed the results back as one user turn.
			const toolUses = assistant.content.filter(b => b.type === 'tool_use') as ToolUseBlock[]
			const results: ContentBlock[] = []
			for (const use of toolUses) {
				// stopped during an earlier tool: skip the rest, they may change the entry
				if (controller.signal.aborted) {
					results.push({ type: 'tool_result', tool_use_id: use.id, content: 'Stopped by the user.', is_error: true })
					continue
				}
				try {
					const out = await executeTool(use.name, use.input, controller.signal)
					results.push({
						type: 'tool_result',
						tool_use_id: use.id,
						content: typeof out === 'string' ? out : JSON.stringify(out ?? null),
					})
				}
				catch (e: any) {
					results.push({
						type: 'tool_result',
						tool_use_id: use.id,
						content: 'Error: ' + (e?.message ?? e),
						is_error: true,
					})
				}
			}
			store.messages.push({ role: 'user', content: results })
			// stopped while a tool ran: don't ask the model again with the aborted signal
			if (controller.signal.aborted) break
		}
	}
	catch (e: any) {
		if (e?.name !== 'AbortError') store.error = e?.message ?? String(e)
	}
	finally {
		store.running = false
		setAbortController(null)
	}
}
